import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Empty, Popover as AntPopover, Spin, Typography, message, Tooltip as AntTooltip } from 'antd';
import { CopyOutlined, ReloadOutlined } from '@ant-design/icons';
import moment from 'moment';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import * as XLSX from 'xlsx';
import {
    Bar,
    BarChart,
    CartesianGrid,
    Cell,
    LabelList,
    ResponsiveContainer,
    XAxis,
    YAxis,
} from 'recharts';
import { getNamespaceGatewayGapsApi } from '../../../api/customerApi';
import { DECODED_ACCESS_TOKEN } from '../../../constants/system.keys';
import storageService from '../../../services/storage.service';
import { getTotalRequiredSecondsPerDevice } from '../../../untils/date';

const { Text } = Typography;

const getBarColor = (percent) => {
    if (percent < 80) return '#ef4444'; // Đỏ (Rất thấp)
    if (percent < 95) return '#f59e0b'; // Cam / Vàng (Cảnh báo)
    return '#0e9347'; // Xanh lá (Tốt)
};

const CustomBarShape = (props) => {
    const { fill, x, y, width, height, payload, t } = props;
    if (!payload) return <rect x={x} y={y} width={width} height={height} fill={fill} rx={4} ry={4} />;

    const handleCopySerial = (e) => {
        if (e) e.stopPropagation();
        if (payload.gatewaySerial) {
            navigator.clipboard.writeText(payload.gatewaySerial);
            message.success(t('Copied serial successfully') || `Copied ${payload.gatewaySerial}`);
        }
    };

    const popoverContent = (
        <div style={{ padding: '4px 6px', maxWidth: '300px' }}>
            <div style={{ fontWeight: 'bold', fontSize: '14px', color: '#60a5fa', marginBottom: '4px' }}>
                {payload.gatewayName || payload.gatewaySerial}
            </div>
            <div style={{ fontSize: '12px', color: '#aaa', marginBottom: '6px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                <span>Serial: {payload.gatewaySerial}</span>
                {payload.gatewaySerial && (
                    <AntTooltip title={t('Copy Serial')}>
                        <CopyOutlined
                            onClick={handleCopySerial}
                            style={{
                                cursor: 'pointer',
                                color: '#60a5fa',
                                fontSize: '14px',
                                padding: '2px',
                            }}
                        />
                    </AntTooltip>
                )}
            </div>
            <div style={{ fontSize: '13px', marginBottom: '2px' }}>
                {t('Upload Rate')}:{' '}
                <strong style={{ color: getBarColor(payload.uploadedPercent) }}>
                    {payload.uploadedPercent}%
                </strong>
            </div>
            <div style={{ fontSize: '12px', color: '#ccc' }}>
                {t('Online camera count')}: {payload.cameraOnline}/{payload.cameraTotal} {t('camera')}
            </div>
            <div style={{ fontSize: '12px', color: '#ccc' }}>
                {t('Gap duration (missing)')}: {payload.totalGapFormatted || `${payload.totalGapS}s`}
            </div>
        </div>
    );

    return (
        <AntPopover content={popoverContent} trigger="hover" placement="right">
            <g style={{ cursor: 'pointer' }} onClick={handleCopySerial}>
                <rect x={x} y={y} width={width} height={height} fill={fill} rx={4} ry={4} />
            </g>
        </AntPopover>
    );
};

const BarLabel = (props) => {
    const { x, y, width, height, value } = props;
    if (value === undefined || value === null) return null;
    return (
        <text
            x={x + width + 8}
            y={y + height / 2 + 4}
            fill="#e5e7eb"
            fontSize="11"
            fontWeight="600"
            textAnchor="start"
        >
            {`${value}%`}
        </text>
    );
};

const formatSecondsToHHMMSS = (totalSeconds) => {
    if (totalSeconds === null || totalSeconds === undefined || isNaN(totalSeconds) || totalSeconds < 0) {
        return '00:00:00';
    }
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = Math.floor(totalSeconds % 60);

    const pad = (num) => String(num).padStart(2, '0');
    return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
};

const GatewaySLA = ({ dateRanger, gateways: propGateways }) => {
    const { t } = useTranslation();
    const { namespace } = useSelector((state) => state);
    const [gatewaysData, setGatewaysData] = useState([]);
    const [loading, setLoading] = useState(false);

    const fromDateStr = useMemo(() => {
        if (!dateRanger || !dateRanger[0]) return moment().format('YYYY-MM-DD');
        return moment(dateRanger[0]).format('YYYY-MM-DD');
    }, [dateRanger]);

    const toDateStr = useMemo(() => {
        if (!dateRanger || !dateRanger[1]) return moment().format('YYYY-MM-DD');
        return moment(dateRanger[1]).format('YYYY-MM-DD');
    }, [dateRanger]);

    const totalDays = useMemo(() => {
        if (!dateRanger || !dateRanger[0] || !dateRanger[1]) return 30;
        const start = moment(dateRanger[0]).startOf('day');
        const end = moment(dateRanger[1]).endOf('day');
        const diff = end.diff(start, 'days') + 1;
        return diff > 0 ? diff : 1;
    }, [dateRanger]);

    const fetchGatewayGaps = useCallback(async () => {
        if (!namespace) return;
        setLoading(true);
        try {
            const token = storageService.getInternalStorageByKey(DECODED_ACCESS_TOKEN);
            const dataQuery = {
                fromDate: fromDateStr,
                toDate: toDateStr,
            };
            const res = await getNamespaceGatewayGapsApi(token, { namespace }, dataQuery);
            if (res && res.data) {
                const apiData = res.data.data || res.data;
                setGatewaysData(apiData.gateways || (Array.isArray(apiData) ? apiData : []));
            } else {
                setGatewaysData([]);
            }
        } catch (err) {
            console.error('Error fetching gateway gaps:', err);
            setGatewaysData([]);
        } finally {
            setLoading(false);
        }
    }, [namespace, fromDateStr, toDateStr]);

    useEffect(() => {
        fetchGatewayGaps();
    }, [fetchGatewayGaps]);

    const effectiveGateways = propGateways && propGateways.length > 0 ? propGateways : gatewaysData;

    const allCalculatedGateways = useMemo(() => {
        if (!effectiveGateways || !Array.isArray(effectiveGateways) || effectiveGateways.length === 0) return [];

        const totalSecondsPerCamera = getTotalRequiredSecondsPerDevice(dateRanger);

        const calculated = effectiveGateways
            .map((gw) => {
                const camerasCount = gw.cameraOnline > 0 ? gw.cameraOnline : (gw.cameraTotal || 0);
                const totalRequiredSeconds = camerasCount * totalSecondsPerCamera;
                const totalGapS = gw.totalGapS || 0;
                const totalUploadS = gw.totalUploadS || 0;
                const uploadedDurationSeconds = Math.max(0, totalRequiredSeconds - totalGapS);
                const unrecoverableDurationS = Math.max(0, totalGapS - totalUploadS);

                let unrecoverableRatePercent = 0;
                if (totalGapS > 0) {
                    unrecoverableRatePercent = Number(((unrecoverableDurationS / totalGapS) * 100).toFixed(2));
                    if (unrecoverableRatePercent > 100) unrecoverableRatePercent = 100;
                    if (unrecoverableRatePercent < 0) unrecoverableRatePercent = 0;
                }

                let uploadedPercent = 100;
                if (totalRequiredSeconds > 0) {
                    uploadedPercent = Number(((uploadedDurationSeconds / totalRequiredSeconds) * 100).toFixed(2));
                    if (uploadedPercent > 100) uploadedPercent = 100;
                    if (uploadedPercent < 0) uploadedPercent = 0;
                } else if (totalGapS > 0) {
                    uploadedPercent = 0;
                }

                let displayName = gw.gatewayName || gw.gatewaySerial || 'N/A';
                if (displayName.length > 32) {
                    displayName = `${displayName.substring(0, 30)}...`;
                }

                return {
                    ...gw,
                    camerasCount,
                    uploadedDurationSeconds,
                    unrecoverableDurationS,
                    unrecoverableRatePercent,
                    uploadedPercent,
                    displayName,
                };
            })
            .filter((gw) => gw.cameraTotal > 0 || gw.cameraOnline > 0 || gw.totalGapS > 0);

        // Sắp xếp tăng dần theo uploadedPercent (tỉ lệ upload thấp nhất lên đầu)
        calculated.sort((a, b) => a.uploadedPercent - b.uploadedPercent);
        return calculated;
    }, [effectiveGateways, dateRanger]);

    const top20LowestGateways = useMemo(() => {
        return allCalculatedGateways.slice(0, 20);
    }, [allCalculatedGateways]);

    const handleExportExcel = useCallback(() => {
        if (!allCalculatedGateways || allCalculatedGateways.length === 0) {
            message.warning(t('No data available to export') || 'No data available to export');
            return;
        }

        try {
            const exportData = allCalculatedGateways.map((gw, index) => ({
                'STT': index + 1,
                [t('Gateway Name') || 'Tên Gateway']: gw.gatewayName || gw.gatewaySerial || 'N/A',
                [t('Gateway Serial') || 'Serial Gateway']: gw.gatewaySerial || 'N/A',
                [t('Online Camera Count') || 'Số Camera Online']: gw.cameraOnline ?? 0,
                [t('Gap Duration') || 'Thời gian thiếu']: formatSecondsToHHMMSS(gw.totalGapS || 0),
                [t('Upload Rate (%)') || 'Tỉ lệ Upload (%)']: gw.uploadedPercent,
                [t('Recoverable Duration') || 'Recoverable duration']: formatSecondsToHHMMSS(gw.totalUploadS || 0),
                [t('Recoverable Rate (%)') || 'Recoverable Rate (%)']: gw.uploadablePercent ?? 0,
                [t('Unrecoverable Duration') || 'Unrecoverable duration']: formatSecondsToHHMMSS(gw.unrecoverableDurationS || 0),
                [t('Unrecoverable Rate (%)') || 'Unrecoverable Rate (%)']: gw.unrecoverableRatePercent ?? 0,
            }));

            const worksheet = XLSX.utils.json_to_sheet(exportData);
            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, 'Gateway SLA');

            const filename = `report_gateway_sla_${namespace}_${fromDateStr}_to_${toDateStr}.xlsx`;
            XLSX.writeFile(workbook, filename);
            message.success(t('Export Excel successfully') || 'Export Excel successfully');
        } catch (err) {
            console.error('Error exporting Excel:', err);
            message.error(t('Export Excel failed') || 'Export Excel failed');
        }
    }, [allCalculatedGateways, namespace, fromDateStr, toDateStr, t]);

    const chartHeight = Math.max(360, top20LowestGateways.length * 30 + 40);

    if (namespace !== "hdbank.com.vn") return null;

    return (
        <div
            className="chart-item"
            style={{
                width: '100%',
                marginTop: '16px',
                backgroundColor: '#141414',
                borderRadius: '8px',
                padding: '16px 20px',
                border: '1px solid #262626',
                position: 'relative',
            }}
        >
            <div
                style={{
                    position: 'absolute',
                    top: '16px',
                    right: '20px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    zIndex: 10,
                }}
            >
                <AntTooltip title={t('Reload')}>
                    <ReloadOutlined
                        onClick={fetchGatewayGaps}
                        style={{
                            color: '#8c8c8c',
                            fontSize: '16px',
                            cursor: 'pointer',
                        }}
                    />
                </AntTooltip>
                <Button
                    onClick={handleExportExcel}
                    style={{
                        borderColor: '#0e9347',
                        color: '#0e9347',
                        backgroundColor: 'transparent',
                        // borderRadius: '4px',
                        fontWeight: '500',
                        fontSize: '13px',
                        height: '28px',
                        padding: '0 12px',
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                    }}
                >
                    {t('Export') || 'Export'}
                </Button>
            </div>
            <div
                style={{
                    display: 'flex',
                    justify: 'space-between',
                    alignItems: 'center',
                    marginBottom: '16px',
                }}
            >
                <Text style={{ fontSize: '16px', fontWeight: 'bold', color: '#fff' }}>
                    {t('TOP 20 GATEWAYS WITH LOWEST UPLOAD RATE')}
                </Text>
            </div>

            {loading ? (
                <div style={{ height: '260px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Spin size="default" />
                </div>
            ) : top20LowestGateways.length === 0 ? (
                <Empty description={t('No Gateway Data')} image={Empty.PRESENTED_IMAGE_SIMPLE} />
            ) : (
                <ResponsiveContainer width="100%" height={chartHeight}>
                    <BarChart
                        layout="vertical"
                        data={top20LowestGateways}
                        margin={{ top: 10, right: 50, left: 10, bottom: 10 }}
                    >
                        <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#262626" />
                        <XAxis
                            type="number"
                            domain={[0, 100]}
                            tick={{ fill: '#8c8c8c', fontSize: 12 }}
                            tickFormatter={(val) => `${val}%`}
                            stroke="#434343"
                        />
                        <YAxis
                            type="category"
                            dataKey="displayName"
                            width={260}
                            tick={{ fill: '#d9d9d9', fontSize: 12 }}
                            stroke="#434343"
                        />
                        <Bar
                            dataKey="uploadedPercent"
                            barSize={16}
                            radius={[0, 4, 4, 0]}
                            shape={<CustomBarShape t={t} />}
                        >
                            {top20LowestGateways.map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={getBarColor(entry.uploadedPercent)} />
                            ))}
                            <LabelList dataKey="uploadedPercent" content={<BarLabel />} />
                        </Bar>
                    </BarChart>
                </ResponsiveContainer>
            )}
        </div>
    );
};

export default GatewaySLA;
